import React from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import styled, { css } from 'styled-components';

import ChevronDownIcon from '../../assets/images/icons/chevron-down.svg';
import { useRejectionReasonTagID } from '../../hooks/useRejectionReasonTagID';
import { EnumVariant, Tag, Tags } from '../../interfaces';
import { useTagsQuery } from '../../queries';
import { DisabledWithTooltip } from '../components/DisabledWithTooltip';
import { Button } from './button';

type ProgressButtonProps = Parameters<typeof Button>[0];

interface Props extends Omit<ProgressButtonProps, 'onClick'> {
  onClick: (tag: Tags) => (evt: any) => void;
  className?: string;
  setOnToggle?: (value: boolean) => void;
  reviewType?: 'internal' | 'external';
  disabled?: boolean;
  isLoading?: boolean;
  disabledTooltip?: string;
}

export function RejectButtonWithReason(props: Props) {
  const { className, onClick, setOnToggle, disabled, disabledTooltip, ...rest } = props;
  const tagsQuery = useTagsQuery();

  const rejectionReasonTagId = useRejectionReasonTagID(props.reviewType);

  const rejectionReasons = tagsQuery.data?.find((tag) => tag.id === rejectionReasonTagId) || ({} as Tag);
  const isLoading = props.isLoading || tagsQuery.isLoading;

  const [isOpened, setIsOpened] = React.useState(false);

  const getTag = (enumVariant: EnumVariant): Tags => {
    const key = `td:${rejectionReasons.id}`;
    const value = `ev:${enumVariant.id}`;
    return {
      [key]: value,
    };
  };

  const options = rejectionReasons?.enum_variants?.filter((option) => option.state === 'active') || [];

  const [activeIndex, setActiveIndex] = React.useState(0);

  const listItems = document?.querySelector('#rejectDropdown')?.childNodes;

  React.useEffect(() => {
    if (listItems?.length) (listItems[activeIndex] as HTMLElement)?.focus();
  }, [activeIndex]);

  const handleArrowKeys = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowUp') {
      setActiveIndex(
        activeIndex === 0 ? options.length - 1 : activeIndex - 1, // If the index goes below 0, wrap around
      );
    }

    if (e.key === 'ArrowDown') {
      setActiveIndex(
        activeIndex === options.length - 1 ? 0 : activeIndex + 1, // If the index goes above the length of the array, wrap around
      );
    }
  };

  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (menuRef && isOpened) {
      menuRef?.current?.childNodes?.[0]?.focus();
      setActiveIndex(0);
    }
  }, [menuRef, isOpened]);

  const onToggle = (isOpen, ev, metadata) => {
    if (metadata.source === 'select') {
      setIsOpened(false);
      setTimeout(() => {
        setOnToggle?.(false);
      }, 200);
      return;
    }
    setIsOpened(isOpen);
    setOnToggle?.(isOpen);
  };

  return (
    // key prop is necessary here, because we're using querySelector
    <Dropdown className="hdn-dropdown" show={isOpened} onToggle={onToggle} key={`isOpened=${isOpened}`}>
      <DisabledWithTooltip disabled={disabled} tooltip={disabledTooltip}>
        <Dropdown.Toggle
          disabled={disabled}
          className={`hdn-dropdown__toggle ${className} custom-btn`}
          as={Button}
          variant="danger"
          {...rest}
        >
          <span className="hdn-dropdown__placeholder">
            {isLoading ? <Spinner size="sm" animation="border" /> : props.children}
          </span>
          <img className="hdn-dropdown__chevron" src={ChevronDownIcon} alt="⌄" />
        </Dropdown.Toggle>
      </DisabledWithTooltip>

      <StyledDropdown id="rejectDropdown" onKeyDown={handleArrowKeys} ref={menuRef} tabIndex={0}>
        {options.map((rr, index) => (
          <Dropdown.Item
            onClick={onClick(getTag(rr))}
            key={rr.id}
            className={`item ${index === activeIndex ? ' active' : ''}`}
            data-accessKey={activeIndex === index ? 'Enter' : undefined}
          >
            {rr.variant_name}
          </Dropdown.Item>
        ))}
      </StyledDropdown>
    </Dropdown>
  );
}

const StyledDropdown = styled(Dropdown.Menu)`
  ${(p) =>
    p.children?.length > 16 &&
    css`
      column-count: 2;
      margin-left: -360px;
    `}
`;
